import AlertBox from '@/components/AlertBox';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { convertISODateToMMDDYYYY } from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';
import GenerateFbsModal from '@/pages/fbs/components/GenerateFbsModal';
import { hasFeature } from '@/services/featureService';
import {
  exportFbsZip,
  getFbs,
  getFbsEmails,
  getFbsUnpaidInvoices,
  useFbsFilterOptions,
  useShareFbsMutation,
} from '@/services/fbsService';
import type { FbsListItem, FbsListQueryParams, FbsListResponse } from '@/types/fbs.types';
import { ColumnDef } from '@tanstack/react-table';
import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownUp,
  BadgeCheck,
  ChevronDown,
  Download,
  Loader2,
  Send,
  SquareArrowUpRight,
  SquareCheckBig,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const FinalBillingSummary = () => {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [tableData, setTableData] = useState<{ data: FbsListItem[] }>();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareFbsIds, setShareFbsIds] = useState<string[]>([]);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const [loadingShareIds, setLoadingShareIds] = useState<string[]>([]);
  const [applyPaymentOpen, setApplyPaymentOpen] = useState(false);
  const [applyPaymentInvoices, setApplyPaymentInvoices] = useState<
    { invoiceNumber: string; invoiceAmount: number; balance?: number | null }[]
  >([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const hasReadFeature = hasFeature('fbs.read');
  const hasGenerateFeature = hasFeature('fbs.generate');
  const hasExportFeature = hasFeature('fbs.export');
  const hasShareFeature = hasFeature('fbs.share');
  const hasApplyPaymentFeature = hasFeature('fbs.applyPayment');

  const shareMutation = useShareFbsMutation();
  const filterOptionsData = useFbsFilterOptions();

  const selectAll = !!tableData?.data?.length && tableData.data.every((row) => selectedIds.includes(row.fbsId));

  const apiGetWrapper = useCallback((params?: unknown, options?: { query?: UseQueryOptions }) => {
    const safeParams = (params || {}) as Record<string, unknown>;
    const { pageNo, fileNumber, ...restParams } = safeParams;
    const normalizedFileNumber =
      typeof fileNumber === 'string' && fileNumber.trim() ? [fileNumber.trim()] : (fileNumber as string[] | undefined);

    const clientPreference = (safeParams.clientPreference as FbsListQueryParams['clientPreference']) ?? 'ALL';

    const apiParams: FbsListQueryParams = {
      ...(restParams as FbsListQueryParams),
      fileNumber: normalizedFileNumber,
      clientPreference,
      page: pageNo as number,
      pageSize: safeParams.pageSize as number,
      sortField: (safeParams.sortField as string) || 'createdAt',
      sortDirection: (safeParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    return getFbs(apiParams, options as { query?: Partial<UseQueryOptions<FbsListResponse>> });
  }, []);

  const { clientOptions, fileNumberOptions } = useMemo(() => {
    const clients = filterOptionsData.data?.clients ?? [];
    const fileNumbers = filterOptionsData.data?.fileNumbers ?? [];
    return {
      clientOptions: clients.map((c) => ({
        ...c,
        label1: c.label1 ?? c.label2 ?? c.value,
      })),
      fileNumberOptions: fileNumbers.map((f) => ({
        ...f,
        label1: f.label1 ?? f.value,
      })),
    };
  }, [filterOptionsData.data]);

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'code',
        label: 'Search Client',
        placeholder: 'Search Client',
        fieldType: 'MULTISELECT',
        multiSelectOptions: clientOptions,
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => date > new Date(),
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => date > new Date(),
        dependsOn: { field: 'fromDate', resetOnChange: true, disableBefore: true },
      },
      {
        name: 'fileNumber',
        label: 'Search File Number',
        placeholder: 'Search File Number',
        fieldType: 'MULTISELECT',
        multiSelectOptions: fileNumberOptions,
      },
    ],
    [clientOptions, fileNumberOptions]
  );

  const handleCheckboxChange = (fbsId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedIds((prev) => (prev.includes(fbsId) ? prev : [...prev, fbsId]));
    } else {
      setSelectedIds((prev) => prev.filter((id) => id !== fbsId));
    }
  };

  const handleSelectAllChange = (isChecked: boolean) => {
    const pageIds = tableData?.data?.map((d) => d.fbsId) ?? [];
    if (isChecked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const openShareDialog = useCallback(async (fbsIds: string[]) => {
    try {
      setLoadingShareIds(fbsIds);
      const emails = await getFbsEmails(fbsIds);
      const all = [...emails.to, ...emails.cc].filter(Boolean);
      if (all.length === 0) {
        toast({
          description: 'No recipient email addresses configured for Final Billing Summary',
          variant: 'destructive',
        });
        return;
      }
      setShareEmails(all);
      setShareFbsIds(fbsIds);
      setShareDialogOpen(true);
    } catch {
      toast({ description: 'Unable to fetch recipient emails', variant: 'destructive' });
    } finally {
      setLoadingShareIds([]);
    }
  }, []);

  const handleApplyPayment = useCallback(async (fbsId: string) => {
    try {
      const data = await getFbsUnpaidInvoices(fbsId);
      if (!data.invoices.length) {
        toast({
          description: 'No confirmed invoices available for applying payment',
          variant: 'destructive',
        });
        return;
      }
      setApplyPaymentInvoices(
        data.invoices.map((inv) => ({
          invoiceNumber: inv.invoiceNumber,
          invoiceAmount: inv.totalAmount,
          balance: inv.balance,
        }))
      );
      setApplyPaymentOpen(true);
    } catch (error) {
      displayError((error as { response?: { data?: unknown } })?.response?.data);
    }
  }, []);

  const handleConfirmShare = () => {
    shareMutation.mutate(
      { fbsIds: shareFbsIds },
      {
        onSuccess: (data) => {
          const sent = data.results.filter((r) => r.status === 'SENT').length;
          const failed = data.results.filter((r) => r.status === 'FAILED').length;
          toast({
            description: failed > 0 ? `${sent} email(s) sent, ${failed} failed` : 'Email has been sent successfully',
          });
          setSelectedIds([]);
          setShareDialogOpen(false);
          setRefreshKey((k) => k + 1);
          void queryClient.invalidateQueries({ queryKey: ['fbs'] });
        },
        onError: (error) => {
          displayError(error.response?.data);
          setShareDialogOpen(false);
        },
      }
    );
  };

  const columns: ColumnDef<FbsListItem>[] = useMemo(
    () => [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all-fbs"
            checked={selectAll}
            onCheckedChange={(checked) => handleSelectAllChange(checked === true)}
          />
        ),
        size: 50,
        cell: ({ row }) => (
          <div className="flex w-full min-w-[40px] mr-2">
            <Checkbox
              id={`fbs-${row.original.fbsId}`}
              checked={selectedIds.includes(row.original.fbsId)}
              onCheckedChange={(checked) => handleCheckboxChange(row.original.fbsId, checked === true)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'clientCode',
        header: 'Client #',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[80px] mr-2">
            <span className="text-black">{cell.getValue() as string}</span>
          </div>
        ),
      },
      {
        accessorKey: 'clientName',
        header: 'Client Name',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col w-full min-w-[140px] mr-2">
            <span className="text-black">{row.original.clientName ?? '-'}</span>
          </div>
        ),
      },
      {
        accessorKey: 'fileNo',
        header: 'File Number',
        enableSorting: true,
        cell: ({ row }) => {
          const { isClosed, isCompleted, isReopened, fileNo, lastEmailSentDate } = row.original;
          return (
            <div className="flex flex-col w-full min-w-[160px] mr-2">
              <div className="flex items-center gap-2">
                <span className="text-black">{fileNo}</span>
                {isReopened && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 cursor-default"
                          aria-label="Reopened"
                          tabIndex={0}
                        >
                          <ArrowDownUp className="h-4 w-4 shrink-0 text-[#551175]" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reopened</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {isCompleted && !isClosed && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 cursor-default"
                          aria-label="Completed"
                          tabIndex={0}
                        >
                          <BadgeCheck className="h-4 w-4 shrink-0 text-[#1551B2]" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completed</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {isClosed && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex shrink-0 cursor-default"
                          aria-label="Closed"
                          tabIndex={0}
                        >
                          <SquareCheckBig className="h-4 w-4 shrink-0 text-[#1B8652]" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Closed</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {lastEmailSentDate && (
                <span className="text-[10px] text-[#969696]">
                  Last email sent on {convertISODateToMMDDYYYY(lastEmailSentDate)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[100px] mr-2">
            <span className="text-black">{convertISODateToMMDDYYYY(cell.getValue() as string)}</span>
          </div>
        ),
      },
      {
        accessorKey: 'totalAmount',
        header: 'Total Amount',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[100px] mr-2">
            <BillingAmount value={cell.getValue() as number} className="text-black" />
          </div>
        ),
      },
      {
        accessorKey: 'totalDueAmount',
        header: 'Due Amount',
        enableSorting: true,
        cell: ({ cell }) => (
          <div className="flex w-full min-w-[100px] mr-2">
            <BillingAmount value={cell.getValue() as number} className="text-black" />
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const item = row.original;
          const canShare = hasShareFeature;
          const showApply = hasApplyPaymentFeature && !item.isClosed;

          return (
            <div className="flex flex-row gap-3 items-center min-w-[200px]">
              {showApply && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-[#1551B2] whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleApplyPayment(item.fbsId);
                  }}
                >
                  Apply Payment
                  <SquareArrowUpRight className="h-4 w-4" />
                </button>
              )}
              {hasExportFeature && (
                <Download
                  className="h-4 w-4 cursor-pointer text-[#0A2240] hover:text-[#1551B2]"
                  onClick={(e) => {
                    e.stopPropagation();
                    void exportFbsZip([item.fbsId]);
                  }}
                />
              )}
              {canShare &&
                (loadingShareIds.includes(item.fbsId) ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[#0A2240]" />
                ) : (
                  <Send
                    className="h-4 w-4 cursor-pointer text-[#0A2240] hover:text-[#1551B2]"
                    onClick={(e) => {
                      e.stopPropagation();
                      void openShareDialog([item.fbsId]);
                    }}
                  />
                ))}
            </div>
          );
        },
      },
    ],
    [
      selectAll,
      selectedIds,
      hasExportFeature,
      hasShareFeature,
      hasApplyPaymentFeature,
      loadingShareIds,
      handleApplyPayment,
      openShareDialog,
    ]
  );

  const bulkActions = useMemo(() => {
    const actions: {
      label: string;
      value: string;
      icon: React.ReactElement;
      onClick: () => void;
    }[] = [];
    if (hasExportFeature) {
      actions.push({
        label: 'Export Billing Summaries',
        value: 'export',
        icon: <Download className="w-4 h-4" color="#334155" strokeWidth="1px" />,
        onClick: () => {
          if (selectedIds.length) void exportFbsZip(selectedIds);
          else toast({ description: 'Please select at least one row to export' });
        },
      });
    }
    if (hasShareFeature) {
      actions.push({
        label: 'Share Billing Summaries',
        value: 'share',
        icon: <Send className="w-4 h-4" color="#334155" strokeWidth="1px" />,
        onClick: () => {
          if (selectedIds.length) void openShareDialog(selectedIds);
          else toast({ description: 'Please select at least one row to share' });
        },
      });
    }
    return actions;
  }, [hasExportFeature, hasShareFeature, selectedIds, openShareDialog]);

  const summarySection = useMemo(() => {
    if (bulkActions.length === 0) return undefined;
    return (
      <div className="w-full flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="focus:border-[#1551B2] !border-[1px] !border-primary min-w-[136px] w-fit h-[36px] !text-primary font-[500] text-sm flex flex-row gap-2 align-middle"
            >
              <span>Bulk Actions</span>
              <ChevronDown className="w-4 h-4" strokeWidth="1px" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 mr-8">
            {bulkActions.map((action) => (
              <DropdownMenuItem key={action.value} onSelect={action.onClick}>
                <div className="!flex !w-full !flex-row justify-between">
                  <span className="text-primary text-sm font-[500]">{action.label}</span>
                  <span>{action.icon}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }, [bulkActions]);

  if (!hasReadFeature) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        key={refreshKey}
        refreshKey={refreshKey}
        toolbar={{
          toolBarTitle: 'Final Billing Summary',
          addButton: hasGenerateFeature,
          addButtonTitle: 'Generate Billing Summary',
          addOnClick: () => setGenerateOpen(true),
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => {
          setTableData({ data: data as FbsListItem[], ...(rest as object) });
        }}
        search={searchConfig}
        summarySection={summarySection}
      />

      <GenerateFbsModal
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        onSuccess={() => {
          setGenerateOpen(false);
          setRefreshKey((k) => k + 1);
          void queryClient.invalidateQueries({ queryKey: ['fbs'] });
        }}
      />

      <AlertBox
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        title="Are you sure?"
        description={
          <span>
            Send the Final Billing Summary to <strong>{shareEmails.join(', ')}</strong>?
          </span>
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Send"
        confirmBtnClick={handleConfirmShare}
        isLoading={shareMutation.isPending}
      />

      <ApplyPaymentModal
        open={applyPaymentOpen}
        onOpenChange={setApplyPaymentOpen}
        invoices={applyPaymentInvoices}
        onSuccess={() => {
          setRefreshKey((k) => k + 1);
          void queryClient.invalidateQueries({ queryKey: ['fbs'] });
          setApplyPaymentOpen(false);
        }}
      />
    </div>
  );
};

export default FinalBillingSummary;
